import { useEffect, useRef, useState } from "react";
import API from "../api";
import urls from "../api/urls";
import {
  enrichShipmentTrackingAuditInfo,
  resolveTrackingAwbNumber,
} from "../utils/enrichShipmentTrackingAuditInfo";

const { AUDIT_TRACKING } = urls;

export const useEnrichedTrackingData = (endpoint, awbNumber) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastParamsKeyRef = useRef(null);

  const paramsKey =
    endpoint && awbNumber ? `${endpoint}|${awbNumber}` : null;

  if (paramsKey && paramsKey !== lastParamsKeyRef.current) {
    lastParamsKeyRef.current = paramsKey;
    if (!loading) setLoading(true);
    if (data !== null) setData(null);
  }

  useEffect(() => {
    if (!endpoint || !awbNumber) {
      setData(null);
      setLoading(false);
      lastParamsKeyRef.current = null;
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const trackingResponse = await API.Api(
          `${endpoint}?awbNumber=${encodeURIComponent(awbNumber)}`
        ).getWithPremiseHeader();

        if (cancelled) {
          return;
        }

        if (!trackingResponse?.status) {
          setData(null);
          return;
        }

        const trackingData = trackingResponse?.response?.data;
        if (!trackingData?.shipmentTracking) {
          setData(trackingData);
          return;
        }

        const resolvedAwb = resolveTrackingAwbNumber(trackingData, awbNumber);
        const auditResponse = await API.Api(
          `${AUDIT_TRACKING}/${encodeURIComponent(resolvedAwb)}`
        ).getWithPremiseHeader();

        if (cancelled) {
          return;
        }

        const auditPayload = auditResponse?.status
          ? auditResponse?.response?.data
          : trackingData?.shipmentTracking?.auditInfo;
        const auditEvents = Array.isArray(auditPayload)
          ? auditPayload
          : auditPayload?.data || auditPayload?.auditInfo || [];

        setData(enrichShipmentTrackingAuditInfo(trackingData, auditEvents));
      } catch {
        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [endpoint, awbNumber]);

  return { data, loading };
};
